/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useReducer, useCallback, type ReactNode } from 'react';
import { getRuntimeConfig } from '@shared/config/runtime';
import type {
  FormState,
  GrantSelection,
  Step1Data,
  Step2Data,
  Step3Data,
  Step4Data,
  BudgetData,
  LoggedInUser,
  KostnadsPoster,
  WalletProvider,
} from '@shared/domain/types';
import { TRUST_SCORE_THRESHOLD } from '@shared/data/mocks/mockCredentials';

const emptyKostnadsPoster: KostnadsPoster = {
  lonn: 0,
  eksterneTjenester: 0,
  utstyrOgMaterialer: 0,
  reiseOgMoter: 0,
  kommunikasjon: 0,
  andreUtgifter: 0,
};

function isMockOnlyVariant(): boolean {
  const variant = getRuntimeConfig().app.variant;
  return variant === 'english' || variant === 'oslo-circular-fast-track';
}

function resolveWalletProvider(provider: WalletProvider): WalletProvider {
  return isMockOnlyVariant() ? 'mock' : provider;
}

function createInitialState(): FormState {
  return {
    partnerRevision: 0,
    step1: {
      virksomhet: '',
      prosjekttittel: '',
      beskrivelse: '',
      problemstilling: '',
      omrader: [],
      samarbeidspartnerLand: undefined,
      samarbeidspartnerOrgnr: undefined,
      samarbeidspartnerNavn: undefined,
    },
    step2: {
      partner: {
        orgnummer: '',
        navn: '',
        kontaktperson: '',
        epost: '',
      },
      requestedProofKeys: [],
      sentCredentials: [],
      simulateRevocation: false,
      threshold: TRUST_SCORE_THRESHOLD,
    },
    step3: {
      tidligereStotte: null,
      partnerDeclaration1: false,
      partnerDeclaration2: false,
      partnerDeclaration3: false,
    },
    step4: {
      declaration1: false,
      declaration2: false,
      declaration3: false,
    },
    budget: {
      finansiering: {
        egenandelKommune: 0,
        egenandelPartner: 0,
        tilskuddSmartOslo: 0,
      },
      kostnaderSoker: { ...emptyKostnadsPoster },
      kostnaderPartner: { ...emptyKostnadsPoster },
    },
    walletProvider: resolveWalletProvider('mock'),
    activeRole: 'kommune',
    sentToPartner: false,
    returnedToKommune: false,
    partnerHasSeenInbox: false,
    isLoggedIn: false,
    selectedGrantProofKeys: [],
  };
}

type FormAction =
  | { type: 'UPDATE_STEP1'; payload: Partial<Step1Data> }
  | { type: 'UPDATE_STEP2'; payload: Partial<Step2Data> }
  | { type: 'UPDATE_STEP3'; payload: Partial<Step3Data> }
  | { type: 'UPDATE_STEP4'; payload: Partial<Step4Data> }
  | { type: 'UPDATE_BUDGET'; payload: Partial<BudgetData> }
  | { type: 'SET_WALLET_PROVIDER'; payload: WalletProvider }
  | { type: 'SET_ROLE'; payload: 'kommune' | 'partner' }
  | { type: 'SELECT_GRANT_CONTEXT'; payload: GrantSelection }
  | { type: 'SEND_TO_PARTNER' }
  | { type: 'RETURN_TO_KOMMUNE' }
  | { type: 'PARTNER_SEEN_INBOX' }
  | { type: 'LOG_IN'; payload: LoggedInUser }
  | { type: 'RESET' };

function updateProject(state: FormState, patch: Partial<Step1Data>): FormState {
  const step1 = { ...state.step1, ...patch };
  const partnerChanged = step1.samarbeidspartnerOrgnr !== state.step1.samarbeidspartnerOrgnr ||
    step1.samarbeidspartnerLand !== state.step1.samarbeidspartnerLand;
  if (!partnerChanged) return { ...state, step1 };

  const initial = createInitialState();
  // A partner's evidence, declarations, budget and signatures cannot be reused
  // for another legal entity, even when its display name happens to match.
  return {
    ...state,
    partnerRevision: state.partnerRevision + 1,
    step1: { ...step1, samarbeidspartnerNavn: patch.samarbeidspartnerNavn },
    step2: {
      ...initial.step2,
      threshold: state.step2.threshold,
      partner: {
        ...initial.step2.partner,
        orgnummer: step1.samarbeidspartnerOrgnr ?? '',
        navn: patch.samarbeidspartnerNavn ?? '',
      },
    },
    step3: initial.step3,
    step4: initial.step4,
    budget: {
      ...state.budget,
      finansiering: { ...state.budget.finansiering, egenandelPartner: 0 },
      kostnaderPartner: initial.budget.kostnaderPartner,
      partnerBudgetFilledAt: undefined,
      kommuneBudgetFilledAt: undefined,
    },
    sentToPartner: false,
    returnedToKommune: false,
    partnerHasSeenInbox: false,
  };
}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'UPDATE_STEP1':
      return updateProject(state, action.payload);
    case 'UPDATE_STEP2':
      return { ...state, step2: { ...state.step2, ...action.payload } };
    case 'UPDATE_STEP3':
      return { ...state, step3: { ...state.step3, ...action.payload } };
    case 'UPDATE_STEP4':
      return { ...state, step4: { ...state.step4, ...action.payload } };
    case 'UPDATE_BUDGET':
      return {
        ...state,
        budget: {
          ...state.budget,
          ...action.payload,
          finansiering: action.payload.finansiering
            ? { ...state.budget.finansiering, ...action.payload.finansiering }
            : state.budget.finansiering,
          kostnaderSoker: action.payload.kostnaderSoker
            ? { ...state.budget.kostnaderSoker, ...action.payload.kostnaderSoker }
            : state.budget.kostnaderSoker,
          kostnaderPartner: action.payload.kostnaderPartner
            ? { ...state.budget.kostnaderPartner, ...action.payload.kostnaderPartner }
            : state.budget.kostnaderPartner,
        },
      };
    case 'SET_WALLET_PROVIDER':
      if (state.walletProvider === resolveWalletProvider(action.payload)) {
        return state;
      }

      return {
        ...state,
        walletProvider: resolveWalletProvider(action.payload),
        step2: {
          ...state.step2,
          requestedProofKeys: [],
          sentCredentials: [],
        },
      };
    case 'SET_ROLE':
      return { ...state, activeRole: action.payload };
    case 'SELECT_GRANT_CONTEXT':
      return {
        ...state,
        selectedChallengeId: action.payload.selectedChallengeId,
        selectedGrantId: action.payload.selectedGrantId,
        selectedGrantTitle: action.payload.selectedGrantTitle,
        selectedGrantThreshold: action.payload.selectedGrantThreshold,
        selectedGrantProofKeys: [...action.payload.selectedGrantProofKeys],
      };
    case 'SEND_TO_PARTNER':
      return { ...state, sentToPartner: true };
    case 'RETURN_TO_KOMMUNE':
      return { ...state, returnedToKommune: true };
    case 'PARTNER_SEEN_INBOX':
      return { ...state, partnerHasSeenInbox: true };
    case 'LOG_IN':
      return { ...state, isLoggedIn: true, loggedInUser: action.payload };
    case 'RESET':
      return { ...createInitialState(), partnerRevision: state.partnerRevision + 1 };
    default:
      return state;
  }
}

interface FormContextType {
  state: FormState;
  updateStep1: (data: Partial<Step1Data>) => void;
  updateStep2: (data: Partial<Step2Data>) => void;
  updateStep3: (data: Partial<Step3Data>) => void;
  updateStep4: (data: Partial<Step4Data>) => void;
  updateBudget: (data: Partial<BudgetData>) => void;
  setWalletProvider: (provider: WalletProvider) => void;
  setRole: (role: 'kommune' | 'partner') => void;
  selectGrantContext: (selection: GrantSelection) => void;
  sendToPartner: () => void;
  returnToKommune: () => void;
  partnerSeenInbox: () => void;
  logIn: (user: LoggedInUser) => void;
  resetDemo: () => void;
}

const FormContext = createContext<FormContextType | null>(null);

export function FormProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(formReducer, undefined, createInitialState);

  const updateStep1 = useCallback((data: Partial<Step1Data>) => {
    dispatch({ type: 'UPDATE_STEP1', payload: data });
  }, []);

  const updateStep2 = useCallback((data: Partial<Step2Data>) => {
    dispatch({ type: 'UPDATE_STEP2', payload: data });
  }, []);

  const updateStep3 = useCallback((data: Partial<Step3Data>) => {
    dispatch({ type: 'UPDATE_STEP3', payload: data });
  }, []);

  const updateStep4 = useCallback((data: Partial<Step4Data>) => {
    dispatch({ type: 'UPDATE_STEP4', payload: data });
  }, []);

  const updateBudget = useCallback((data: Partial<BudgetData>) => {
    dispatch({ type: 'UPDATE_BUDGET', payload: data });
  }, []);

  const setWalletProvider = useCallback((provider: WalletProvider) => {
    dispatch({ type: 'SET_WALLET_PROVIDER', payload: provider });
  }, []);

  const setRole = useCallback((role: 'kommune' | 'partner') => {
    dispatch({ type: 'SET_ROLE', payload: role });
  }, []);

  const selectGrantContext = useCallback((selection: GrantSelection) => {
    dispatch({ type: 'SELECT_GRANT_CONTEXT', payload: selection });
  }, []);

  const sendToPartner = useCallback(() => {
    dispatch({ type: 'SEND_TO_PARTNER' });
  }, []);

  const returnToKommune = useCallback(() => {
    dispatch({ type: 'RETURN_TO_KOMMUNE' });
  }, []);

  const partnerSeenInbox = useCallback(() => {
    dispatch({ type: 'PARTNER_SEEN_INBOX' });
  }, []);

  const logIn = useCallback((user: LoggedInUser) => {
    dispatch({ type: 'LOG_IN', payload: user });
  }, []);

  const resetDemo = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return (
    <FormContext.Provider
      value={{
        state,
        updateStep1,
        updateStep2,
        updateStep3,
        updateStep4,
        updateBudget,
        setWalletProvider,
        setRole,
        selectGrantContext,
        sendToPartner,
        returnToKommune,
        partnerSeenInbox,
        logIn,
        resetDemo,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}

export function useFormState() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormState must be used within a FormProvider');
  }
  return context;
}
